import { BolsasService } from './bolsasService';
import { EstadoBolsa } from '../pages/CentroCotizaciones/state/bolsaMachine';

// ─────────────────────────────────────────────────────────────────────────────
// Destinos de aterrizaje de las acciones de bolsas. Hay DOS contratos, a propósito:
//
//  1) resolverDestinoDetalle({abrirCrear})  ← ítems del MENÚ LATERAL "Crear" y "Bolsas".
//     SIEMPRE aterrizan en el Detalle de Cotización (que lista las cotizaciones en borrador
//     en su franja, SPEC-205). "Crear" además abre el pop-up de Nueva Bolsa. NUNCA cae a Centro ni a
//     Catálogo: sin bolsas, aterriza igual en el Detalle en su estado vacío.
//     Este es el contrato que el usuario pidió fijar para el menú lateral. No volver a
//     divergirlo ni reintroducir fallbacks a Centro/Catálogo (tests-candado abajo).
//
//  2) resolverDestinoCrear()  ← botón "Nueva bolsa" DENTRO del Centro de Cotizaciones
//     (SPEC-124/128/138). Mantiene su UX certificada: si hay una bolsa abierta va a su
//     detalle con el flag; si no, abre el modal EN el propio Centro (no navega). No es el
//     menú lateral, por eso conserva comportamiento propio.
// ─────────────────────────────────────────────────────────────────────────────

export interface DestinoDetalle {
  path: string;
  state?: { autoOpenNuevaBolsaModal: true };
}

// Compat: nombre histórico del tipo (lo importan App.tsx y tests previos).
export type DestinoCrear = DestinoDetalle;

// ── Contrato 1: MENÚ LATERAL ("Crear" y "Bolsas") ───────────────────────────

// Ruta del Detalle cuando NO hay ninguna cotización en borrador: el propio Detalle se
// renderiza en su estado vacío ("No hay cotizaciones en borrador") y, si abrirCrear, abre el pop-up.
const DETALLE_VACIO = '/cotizaciones/detalle';

// Fuente única de aterrizaje para los ítems "Crear" y "Bolsas" del menú lateral.
// SPEC-205 (RN-009, reemplaza el criterio de SPEC-141): mismo criterio que la franja.
//   - hay cotizaciones BORRADOR → /cotizaciones/detalle/{la más reciente}
//   - no hay ninguna             → /cotizaciones/detalle (estado vacío del Detalle)
//   - la consulta falla          → /cotizaciones/detalle (estado vacío), sin bloquear
// Si abrirCrear=true, adjunta autoOpenNuevaBolsaModal (mecanismo de SPEC-115).
// listarBolsas() (GET /bolsas) es la MISMA fuente scoped con cuyos ids carga el
// Detalle (GET /bolsas/:id), así que todo id entregado resuelve por construcción (SPEC-146).
export async function resolverDestinoDetalle(
  opts: { abrirCrear: boolean } = { abrirCrear: false },
): Promise<DestinoDetalle> {
  const state = opts.abrirCrear ? ({ autoOpenNuevaBolsaModal: true } as const) : undefined;
  const conState = (path: string): DestinoDetalle => (state ? { path, state } : { path });

  try {
    const borradores = await BolsasService.listarBolsas({ estado: EstadoBolsa.BORRADOR });
    if (borradores.length > 0) {
      return conState(`/cotizaciones/detalle/${borradores[0].id}`);
    }
    return conState(DETALLE_VACIO);
  } catch {
    return conState(DETALLE_VACIO);
  }
}

// ── Contrato 2: botón "Nueva bolsa" DENTRO del Centro (SPEC-124/128/138) ─────

// "Bolsa abierta" para el botón del Centro: mismo criterio de la franja de chips
// (ACTIVA o BORRADOR). No confundir con `esListable` del menú lateral.
const ESTADOS_ABIERTOS = new Set<string>([EstadoBolsa.ACTIVA, EstadoBolsa.BORRADOR]);

// Fallback del botón del Centro: permanece en el Centro con el modal auto-abierto
// (SPEC-128). El Centro detecta que el destino es su propia ruta y abre el modal en
// sitio en vez de navegar. Distinto del menú lateral a propósito (ver cabecera).
const FALLBACK_CENTRO: DestinoDetalle = {
  path: '/cotizaciones',
  state: { autoOpenNuevaBolsaModal: true },
};

export async function resolverDestinoCrear(): Promise<DestinoDetalle> {
  try {
    const bolsas = await BolsasService.listarBolsas();
    const abiertas = bolsas.filter((b) => ESTADOS_ABIERTOS.has(b.estado_codigo ?? ''));
    if (abiertas.length > 0) {
      return {
        path: `/cotizaciones/detalle/${abiertas[0].id}`,
        state: { autoOpenNuevaBolsaModal: true },
      };
    }
    return FALLBACK_CENTRO;
  } catch {
    return FALLBACK_CENTRO;
  }
}
